import { createElement } from 'react';
import { captureException } from '@sentry/react';
import { TbCloudLock, TbCloudLockOpen } from 'react-icons/tb';
import { StateCreator } from 'zustand';

import { workspaceService } from '@/services/database';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    appendEntity,
    mergeEntity,
    mergeEntityById,
    prependEntity,
    removeEntityById,
    replaceEntityById,
} from '@/stores/helpers/collections';
import { reduceEntityDelta } from '@/stores/helpers/delta';
import { publishLocalDeltaPacket } from '@/stores/realtime';
import { Workspace } from '@/types';

import { WorkspaceActions, WorkspaceState } from '../types';

export const createWorkspaceActions: StateCreator<
    WorkspaceState & WorkspaceActions,
    [['zustand/immer', never]],
    [],
    WorkspaceActions
> = (set, get) => ({
    // Core
    fetchWorkspaces: async (params) => {
        set({ loading: true });

        try {
            const workspaces = await workspaceService.list(params);

            set({ workspaces, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load workspaces' });
        }
    },
    fetchActiveWorkspace: async () => {
        set((state) => {
            state.loadingWorkspace = true;
            state.error = undefined;
        });

        try {
            const workspace = await workspaceService.getActive();

            set({
                selectedWorkspace: workspace,
                loadingWorkspace: false,
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch workspace detail';
                state.loadingWorkspace = false;
            });
        }
    },
    fetchWorkspaceById: async (id) => {
        set((state) => {
            state.loadingWorkspace = true;
            state.error = undefined;
        });

        try {
            const workspace = await workspaceService.getById(id);

            set({
                selectedWorkspace: workspace,
                loadingWorkspace: false,
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch workspace detail';
                state.loadingWorkspace = false;
            });
        }
    },
    clearSelectedWorkspace: () => {
        set((state) => {
            state.selectedWorkspace = null;
        });
    },
    setSwitchingWorkspace: (switchingWorkspace) => {
        set((state) => {
            state.switchingWorkspace = switchingWorkspace;
        });
    },
    createWorkspace: async (input) => {
        const tempId = `temp-${Date.now()}`;
        const optimistic = { id: tempId, ...input } as Workspace;

        set({ workspaces: appendEntity(get().workspaces, optimistic) });

        publishLocalDeltaPacket({
            v: 1,
            type: 'delta:packet',
            entity: 'workspace',
            operation: 'create',
            entityId: tempId,
            payload: optimistic,
            timestamp: Date.now(),
        });

        try {
            const workspace = await workspaceService.create(input);

            set({
                workspaces: replaceEntityById(get().workspaces, tempId, workspace),
                activeWorkspaces: prependEntity(get().activeWorkspaces, workspace),
            });

            publishLocalDeltaPacket({
                v: 1,
                type: 'delta:packet',
                entity: 'workspace',
                operation: 'delete',
                entityId: tempId,
                timestamp: Date.now(),
            });

            publishLocalDeltaPacket({
                v: 1,
                type: 'delta:packet',
                entity: 'workspace',
                operation: 'create',
                entityId: workspace.id,
                payload: workspace,
                timestamp: Date.now(),
            });

            return workspace;
        } catch (error) {
            captureException(error);

            set({ workspaces: removeEntityById(get().workspaces, tempId), error: 'Failed to create workspace' });

            publishLocalDeltaPacket({
                v: 1,
                type: 'delta:packet',
                entity: 'workspace',
                operation: 'delete',
                entityId: tempId,
                timestamp: Date.now(),
            });
        }
    },
    updateWorkspace: async (id, updates) => {
        const prev = structuredClone(get().workspaces.find((f) => f.id === id));

        // if (!prev) {
        //     return;
        // }

        set({
            workspaces: mergeEntityById(get().workspaces, id, updates),
            activeWorkspaces: mergeEntityById(get().activeWorkspaces, id, updates),
            pendingWorkspaces: mergeEntityById(get().pendingWorkspaces, id, updates),
            selectedWorkspace:
                get().selectedWorkspace?.id === id
                    ? mergeEntity(get().selectedWorkspace!, updates)
                    : get().selectedWorkspace,
        });

        publishLocalDeltaPacket({
            v: 1,
            type: 'delta:packet',
            entity: 'workspace',
            operation: 'update',
            entityId: id,
            payload: updates,
            timestamp: Date.now(),
        });

        try {
            const workspace = await workspaceService.update(id, updates);

            set({
                workspaces: replaceEntityById(get().workspaces, id, workspace),
                activeWorkspaces: replaceEntityById(get().activeWorkspaces, id, workspace),
                pendingWorkspaces: replaceEntityById(get().pendingWorkspaces, id, workspace),
                selectedWorkspace: get().selectedWorkspace?.id === id ? workspace : get().selectedWorkspace,
            });

            publishLocalDeltaPacket({
                v: 1,
                type: 'delta:packet',
                entity: 'workspace',
                operation: 'update',
                entityId: id,
                payload: workspace,
                timestamp: Date.now(),
            });
        } catch (error) {
            captureException(error);

            set({
                workspaces: prev ? replaceEntityById(get().workspaces, id, prev) : get().workspaces,
                activeWorkspaces: prev ? replaceEntityById(get().activeWorkspaces, id, prev) : get().activeWorkspaces,
                pendingWorkspaces: prev
                    ? replaceEntityById(get().pendingWorkspaces, id, prev)
                    : get().pendingWorkspaces,
                selectedWorkspace: prev && get().selectedWorkspace?.id === id ? prev : get().selectedWorkspace,
                error: 'Failed to update workspace',
            });

            if (prev) {
                publishLocalDeltaPacket({
                    v: 1,
                    type: 'delta:packet',
                    entity: 'workspace',
                    operation: 'update',
                    entityId: id,
                    payload: prev,
                    timestamp: Date.now(),
                });
            }
        }
    },
    deleteWorkspace: async (id) => {
        const prev = get().workspaces;
        const deleted = structuredClone(get().workspaces.find((f) => f.id === id));

        set((state) => {
            state.workspaces = state.workspaces.filter((f) => f.id !== id);

            // Clear selectedWorkspace if it's the deleted one
            if (state.selectedWorkspace?.id === id) {
                state.selectedWorkspace = null;
            }
        });

        publishLocalDeltaPacket({
            v: 1,
            type: 'delta:packet',
            entity: 'workspace',
            operation: 'delete',
            entityId: id,
            timestamp: Date.now(),
        });

        try {
            await workspaceService.delete(id);
        } catch (error) {
            captureException(error);

            set({ workspaces: prev, error: 'Failed to delete workspace' });

            if (deleted) {
                publishLocalDeltaPacket({
                    v: 1,
                    type: 'delta:packet',
                    entity: 'workspace',
                    operation: 'create',
                    entityId: id,
                    payload: deleted,
                    timestamp: Date.now(),
                });
            }
        }
    },
    applyWorkspaceDelta: (packet) => {
        if (packet.entity !== 'workspace') {
            return;
        }

        const workspaces = reduceEntityDelta<Workspace>(get().workspaces, get().selectedWorkspace, packet, {
            prependCreated: true,
        });
        const activeWorkspaces = reduceEntityDelta<Workspace>(get().activeWorkspaces, null, packet, {
            prependCreated: true,
        });

        if (!workspaces || !activeWorkspaces) {
            return;
        }

        set({
            workspaces: workspaces.items,
            activeWorkspaces: activeWorkspaces.items,
            pendingWorkspaces: removeEntityById(get().pendingWorkspaces, packet.entityId),
            selectedWorkspace: workspaces.selected,
        });
    },
    // Aliases
    fetchActiveWorkspaces: async (params) => {
        set({ loading: true });

        try {
            const activeWorkspaces = await workspaceService.listActive(params);

            set({ activeWorkspaces, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load workspaces' });
        }
    },
    fetchPendingWorkspaces: async (params) => {
        set({ loading: true });

        try {
            const pendingWorkspaces = await workspaceService.listPending(params);

            set({ pendingWorkspaces, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load workspaces' });
        }
    },
    toggleWorkspaceSAMLSSO: async (id, samlEnabled) => {
        const prev = structuredClone(get().workspaces.find((f) => f.id === id));
        const prevSelectedWorkspace = structuredClone(get().selectedWorkspace);

        if (!prev) {
            return;
        }

        set((state) => {
            const workspace = state.workspaces.find((f) => f.id === id);
            if (workspace) {
                workspace.samlEnabled = samlEnabled;
            }

            // Also update selectedWorkspace if it matches
            if (state.selectedWorkspace?.id === id) {
                state.selectedWorkspace.samlEnabled = samlEnabled;
            }
        });

        try {
            const updatedWorkspace = await workspaceService.setSamlSso(id, samlEnabled);

            set((state) => {
                const workspaceIndex = state.workspaces.findIndex((workspace) => workspace.id === id);

                if (workspaceIndex >= 0) {
                    Object.assign(state.workspaces[workspaceIndex] as Workspace, updatedWorkspace);
                }

                if (state.selectedWorkspace?.id === id) {
                    Object.assign(state.selectedWorkspace as Workspace, updatedWorkspace);
                }
            });

            toast(`${samlEnabled ? 'Enabled SAML SSO' : 'Disabled SAML SSO'}`, {
                icon: createElement(samlEnabled ? TbCloudLock : TbCloudLockOpen, { size: '1rem' }),
                description: updatedWorkspace.name,
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const workspaceIndex = state.workspaces.findIndex((workspace) => workspace.id === id);

                if (workspaceIndex >= 0) {
                    Object.assign(state.workspaces[workspaceIndex] as Workspace, prev);
                }

                if (prevSelectedWorkspace?.id === id) {
                    if (state.selectedWorkspace) {
                        Object.assign(state.selectedWorkspace as Workspace, prevSelectedWorkspace);
                    }
                }

                state.error = 'Failed to update SAML';
            });
        }
    },
});
